import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { ExtractStringKeysFromTypeWhereValueIsType } from '@shared/client/models/common.model';
import { FileTypeEnum, FormErrorEnum, FormGroupType } from '@shared/client/models/form.model';
import { FileService } from '@shared/client/services/file.service';
import { FormService } from '@shared/client/services/form.service';

export type inputTextValidator = { maxLength?: number; minLength?: number };

@Injectable({
    providedIn: 'root',
})
export class CommonValidatorsService {
    public static forbiddenCharsValidator(forbiddenChars: string[]): ValidatorFn {
        const escapedChars = forbiddenChars.map((char) => char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('');

        const regex = new RegExp(`[${escapedChars}]`);

        return (control: AbstractControl): ValidationErrors | null => {
            const value: string = control.value;

            if (!value) {
                return null;
            }

            return regex.test(value)
                ? {
                      [FormErrorEnum.INVALID_CHARACTERS]: { forbiddenChars: forbiddenChars, found: true },
                  }
                : null;
        };
    }

    public static forbiddenWords(forbiddenWords: string[]): ValidatorFn {
        return (control: AbstractControl<string>): ValidationErrors | null => {
            const value: string = control.value;

            if (!value) {
                return null;
            }

            const normalizedValue: string = value.trim().toLowerCase();

            const matchedWord: string | undefined = forbiddenWords.find(
                (word: string): boolean => word.toLowerCase() === normalizedValue,
            );

            if (!matchedWord) {
                return null;
            }

            return {
                [FormErrorEnum.FORBIDDEN_WORD]: {
                    word: matchedWord,
                },
            };
        };
    }

    public static getTextLength(value: string): number {
        return value.length + (value.split('\n').length - 1);
    }

    public static integer(): ValidatorFn {
        return (control: AbstractControl<number | string>): ValidationErrors | null => {
            const value = control.value;

            if (!value || value === '') {
                return null;
            }

            const numericValue = Number(value);

            if (!Number.isInteger(numericValue)) {
                return {
                    [FormErrorEnum.INVALID_NUMBER]: true,
                };
            }

            return null;
        };
    }

    public static maxFileSize(fileType: FileTypeEnum, size?: number): ValidatorFn {
        let maxSize: number;
        if (size) {
            maxSize = size;
        } else {
            maxSize = fileType === FileTypeEnum.IMAGE ? FormService.maxImageSize : FormService.maxFileSize;
        }

        return (control: AbstractControl<File[] | string | null>): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            let isValid: boolean;
            if (typeof control.value === 'string') {
                const blob = FileService.convertBase64ToBlob(control.value);

                isValid = blob.size <= maxSize;
            } else {
                isValid = control.value.every((file: File) => file.size <= maxSize);
            }

            const formErrorKey =
                fileType === FileTypeEnum.IMAGE ? FormErrorEnum.MAX_IMAGE_SIZE : FormErrorEnum.MAX_FILE_SIZE;

            return isValid
                ? null
                : {
                      [formErrorKey]: true,
                  };
        };
    }

    public static maxLength(maxLength: number): ValidatorFn {
        return (control: AbstractControl<string>): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            const textLength = this.getTextLength(control.value);

            return textLength <= maxLength ? null : { [FormErrorEnum.MAX_LENGTH]: true };
        };
    }

    public static onlyAlphaNumeric(exceptions?: string[]) {
        return (control: AbstractControl<string>): ValidationErrors | null => {
            const value: string = control.value;

            if (!value) {
                return null;
            }

            const normalizedValue: string = value.trim().toLowerCase();

            if (value.length !== normalizedValue.length || normalizedValue.split(' ').length > 1) {
                return {
                    [FormErrorEnum.INVALID_CHARACTERS]: {
                        forbiddenChars: ['␣'],
                        found: true,
                    },
                };
            }

            const regex = new RegExp(`^[a-z0-9${exceptions?.join('')}]*$`);

            if (!regex.test(normalizedValue)) {
                return {
                    [FormErrorEnum.ONLY_UNACCENT_AND_ALPHANUMERIC]: {
                        exceptions: exceptions,
                        found: true,
                    },
                };
            }

            return null;
        };
    }

    public static stringValidationForm(options?: inputTextValidator): ValidatorFn[] {
        return [
            Validators.required,
            ...[
                CommonValidatorsService.trimValidator({
                    minLength: options?.minLength,
                }),
            ],
            ...(options?.minLength ? [Validators.minLength(options.minLength)] : []),
            ...(options?.maxLength ? [Validators.maxLength(options.maxLength)] : []),
        ];
    }

    public static supportedFile(acceptedFileExtensions: string[]): ValidatorFn {
        return (control: AbstractControl<File[] | string | null>): ValidationErrors | null => {
            if (!control.value?.length) {
                return null;
            }

            let isSupported: boolean;
            if (typeof control.value === 'string') {
                isSupported = CommonValidatorsService.isSupportedBase64(control.value, acceptedFileExtensions);
            } else {
                isSupported = CommonValidatorsService.isSupportedFileInput(control.value, acceptedFileExtensions);
            }

            return isSupported
                ? null
                : {
                      [FormErrorEnum.UNSUPPORTED_FILETYPE]: {
                          value: control.value,
                      },
                  };
        };
    }

    public static trimValidator(options: { minLength: number | undefined }): ValidatorFn {
        return (control: AbstractControl<string>): ValidationErrors | null => {
            const value = control.value;

            if (!value) {
                return null;
            }

            if (!value.trim()) {
                return { onlyWhitespace: true };
            }

            if (value.endsWith(' ') && options?.minLength && value.length === options.minLength) {
                return {
                    minLengthWithTrailingSpaces: true,
                };
            }

            if (value.startsWith(' ')) {
                control.setValue(control.value?.trimStart(), { emitEvent: false, onlySelf: true });
            }

            const redundantSpacesEndLineRegex: RegExp = /\s{2,}$/;
            if (redundantSpacesEndLineRegex.test(value)) {
                control.setValue(control.value?.trimEnd(), { emitEvent: false, onlySelf: true });
            }

            return null;
        };
    }

    public static valuesEqual<T extends FormGroupType, U extends string | number>(
        control: AbstractControl<T>,
        keys: ExtractStringKeysFromTypeWhereValueIsType<T, FormControl<U>>[],
    ): ValidationErrors | null {
        if (keys.length < 2) {
            return null;
        }

        const firstValue = control.get(keys[0])?.value;
        for (const key of keys.slice(1)) {
            const testedValue = control.get(key)?.value;

            if (firstValue !== testedValue) {
                return { [FormErrorEnum.MISMATCH]: true };
            }
        }

        return null;
    }

    private static isSupportedBase64(dataUrl: string, acceptedFileExtensions: string[]): boolean {
        const metadata = dataUrl.split(',')[0];
        const mimetype = metadata.split(';')[0].split(':')[1];

        const fileType = mimetype.split('/')[1];

        return acceptedFileExtensions.includes(`.${fileType}`);
    }

    private static isSupportedFileInput(files: File[], acceptedFileExtensions: string[]): boolean {
        return files.every((file: File) => {
            const fileExtension = file.name.split('.').reverse()[0];

            return file.size > 0 && acceptedFileExtensions.includes(`.${fileExtension}`);
        });
    }
}
